import { useCallback } from 'react';

import { type ApplicationState, useApplicationState } from '@lib/state';
import { type MfaFunctions, type MfaResult } from '@lib/state/Mfa';

const selectMfa = (state: ApplicationState) => state.mfa;

/**
 * Hook for components (modal, callers) to interact with the MFA controller slice.
 *
 * See plan unit U2 in `docs/plans/2026-05-01-001-feat-decouple-mfa-from-auth-navigation-plan.md`.
 *
 * For sagas / axios interceptors that cannot use hooks, use `getMfaChallenge()` below.
 */
const useMfaChallenge = () => {
    const { inflight, challenge, awaitInflight, resolveInflight } = useApplicationState(selectMfa);

    /** Resolve the current in-flight challenge as a user-initiated Skip. */
    const skip = useCallback(() => {
        resolveInflight({ status: 'skipped' });
    }, [resolveInflight]);

    return {
        inflight,
        request: inflight?.request,
        challenge,
        awaitInflight,
        resolve: resolveInflight,
        skip
    };
};

/**
 * Non-hook accessor for sagas and the 428 interceptor. Returns the live MFA slice so
 * callers can dispatch challenges or resolve in-flight ones outside the React tree.
 *
 * Returns an object exposing the same functions as the hook plus the current `inflight`
 * snapshot. Use the slice methods (challenge / awaitInflight / resolveInflight) — do not
 * mutate `inflight` directly.
 */
/**
 * True when the MFA slice already holds an inflight challenge.
 *
 * Callers that `beginMfa` before `dispatchMfa` MUST run this check, `beginMfa` and the
 * dispatch in one synchronous block — no `await` between them. `challenge()` claims the slot
 * synchronously, so that ordering makes check-and-claim atomic: a losing caller never stages
 * anything and never overwrites the winner's `pendingMfa.authToken` (JSAD-7388).
 */
export const isMfaInflight = (): boolean => !!useApplicationState.getState().mfa?.inflight;

/**
 * Non-hook read of the `mfa-device-verification` master-flag mirror (written by
 * MfaChallengeHost). For sagas / Zustand actions / interceptors that cannot call
 * `useFeature`. Defaults to `false` if the slice hasn't hydrated yet.
 *
 * React code should use `useFeature('mfa-device-verification')` directly instead.
 */
export const getMfaEnabled = (): boolean =>
    useApplicationState.getState().mfa?.isMfaEnabled ?? false;

/**
 * Non-hook read of the `mfa-account-verification` flag mirror (written by MfaChallengeHost).
 * For sagas / Zustand actions / the CheckoutService endpoints that cannot call `useFeature`.
 * Defaults to `false` if the slice hasn't hydrated yet. React code should use
 * `useFeature('mfa-account-verification')` directly instead.
 */
export const getAccVerificationEnabled = (): boolean =>
    useApplicationState.getState().mfa?.isAccVerificationEnabled ?? false;

export const getMfaChallenge = (): MfaFunctions & {
    inflight: ApplicationState['mfa']['inflight'];
} => {
    const slice = useApplicationState.getState().mfa;
    return {
        inflight: slice.inflight,
        challenge: slice.challenge,
        awaitInflight: slice.awaitInflight,
        resolveInflight: slice.resolveInflight,
        setMfaEnabled: slice.setMfaEnabled,
        setAccVerificationEnabled: slice.setAccVerificationEnabled
    };
};

export type { MfaResult };

export default useMfaChallenge;
